import { ChevronDown } from 'lucide-react';
import Link from 'next/link';
import { IconButton } from '../../ui';
import { ConversationsState } from '../../../../lib/conversations';

import { useConversationActions, CONVERSATIONS_PAGE, type ConversationActionsFlow } from '../../../../lib/use-conversation-actions';
import { ConversationRowMenu } from '../../conversations/ConversationRowMenu';
import { DeleteConversationConfirm } from '../../conversations/DeleteConversationConfirm';
import { RenameConversationDialog } from '../../conversations/RenameConversationDialog';
import { PinIcon } from './icons';
import { useRecentChats, type RecentChatModel } from '../hooks/use-recent-chats';
import { RECENT_CHATS_DEFAULT_OPEN } from '../shared';
import styles from './ConversationRow.module.css';
import { GroupCompactRow } from '../../group-chat/GroupAvatars';
import { SeeAllChatsLink } from './SeeAllChatsLink';
import { WhatsAppGlyph } from '../../settings-whatsapp/WhatsAppGlyph';

function ConversationItem({ chat, flow }: { chat: RecentChatModel; flow: ConversationActionsFlow }) {
  return (
    <div className={styles.row}>
    <Link
      href={chat.route}
      as={chat.path}
      shallow
      onClick={chat.onClick}
      aria-current={chat.isSelected ? 'page' : undefined}
      className={`orion-nav-item ${styles.link}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        margin: 0,
        paddingBlock: 7,
        paddingInlineEnd: 8,
        paddingInlineStart: 16,
        borderRadius: 8,
        cursor: 'pointer',
        textDecoration: 'none',
      }}
    >
      {chat.group ? (
        <span className={`${styles.body} ${styles.title}`} style={{ color: 'inherit' }}>
          <GroupCompactRow summary={chat.group} title={chat.title} />
        </span>
      ) : (
        <span className={styles.body}>
          {chat.waGroup ? (
            <span style={{ display: 'flex', alignItems: 'center', minWidth: 0, maxWidth: '100%' }}>
              <span aria-hidden style={{ display: 'inline-flex', flexShrink: 0, marginInlineEnd: 6 }}><WhatsAppGlyph size={14} /></span>
              <span className={styles.title}>{chat.title}</span>
            </span>
          ) : (
            <span className={styles.title}>{chat.title}</span>
          )}
          {chat.channelBadge && <span className={styles.channel}>{chat.channelBadge}</span>}
        </span>
      )}
    </Link>
      <span className={styles.menu}>
        <ConversationRowMenu conv={chat.conv} title={chat.title} flow={flow} />
      </span>
    </div>
  );
}

// The WIDE rail's Pinned Chats section — a collapsible list of the pinned threads,
// open by default. Which threads and where each goes lives in useRecentChats; the
// mobile drawer renders the same model with its own markup.
function ChatGroup({
  state,
  selectedId,
  collapsed = false,
  flow,
}: {
  state: ConversationsState;
  selectedId: string | null;
  collapsed?: boolean;
  flow: ConversationActionsFlow;
}) {
  const { chats, open, toggle, label } = useRecentChats({ state, selectedId, defaultOpen: RECENT_CHATS_DEFAULT_OPEN, section: 'pinned' });
  // An empty group renders nothing at all — a permanently-visible "Pinned Chats" header over no rows
  // is a section that only ever states its own emptiness.
  if (chats.length === 0) return null;

  return (
    <div className="orion-rail-fade" style={{ flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '4px 0 8px' }}>
        {/* Pinned Chats header — in collapsed: IconButton pill; in expanded: full-width row */}
        {collapsed ? (
          <IconButton
            aria-label={label}
            aria-current={selectedId ? 'page' : undefined}
            size="large"
            onClick={toggle}
          >
            <PinIcon />
          </IconButton>
        ) : (
          <button
            type="button"
            onClick={toggle}
            aria-expanded={open}
            className="orion-nav-item"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              width: '100%',
              boxSizing: 'border-box',
              margin: 0,
              paddingBlock: 5,
              paddingInline: 8,
              borderRadius: 8,
              border: 'none',
              cursor: 'pointer',
              textAlign: 'start',
              fontFamily: 'inherit',
              fontSize: 14,
              fontWeight: 'var(--sym-weight-semibold)',
              color: 'var(--ds-text-primary)',
            }}
          >
            <span aria-hidden style={{ display: 'inline-flex', flexShrink: 0, color: 'var(--ds-icon-primary)', width: 24, height: 24, alignItems: 'center', justifyContent: 'center' }}>
              <PinIcon />
            </span>
            <span style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
              {label}
              <ChevronDown
                aria-hidden
                size={12}
                style={{ marginInlineStart: 'auto', flexShrink: 0, transition: 'transform 200ms ease', transform: open ? 'rotate(180deg)' : 'none', color: 'var(--ds-icon-primary)' }}
              />
            </span>
          </button>
        )}
        {open && (
          <div className="orion-rail-fade" style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
            {chats.map((chat) => (
              <ConversationItem key={chat.conv.id} chat={chat} flow={flow} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/// The rail's conversation group: Pinned Chats, then "See all chats".
///
/// **The rail lists PINNED chats and nothing else** (Figma "Menu"
/// 18693:74550, which draws Pinned Chats and then straight on to the plan card). The Chats screen IS the
/// chat history — a searchable, filterable, paginated list of every thread — so a ten-row recency list in
/// the rail is a worse copy of the screen one nav item above it, and the two disagreed the moment the
/// screen could filter. A pin is the opposite: the user saying "keep this one within reach", which no
/// list-of-everything can express.
///
/// "See all chats" stays — it is a link to the list, not a copy of it.
///
/// The delete confirm is mounted ONCE here rather than inside the group's rows — two mounted dialogs sharing
/// one flow would both open on the same request and fight over the overlay.
export function ConversationsSection({
  state,
  selectedId,
  collapsed = false,
}: {
  state: ConversationsState;
  selectedId: string | null;
  collapsed?: boolean;
}) {
  const flow = useConversationActions(CONVERSATIONS_PAGE);
  return (
    <>
      <ChatGroup state={state} selectedId={selectedId} collapsed={collapsed} flow={flow} />
      {!collapsed && <SeeAllChatsLink />}
      <DeleteConversationConfirm flow={flow} />
      <RenameConversationDialog flow={flow} />
    </>
  );
}
